import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';
import Modal from '../Modal/Modal';
import { AchievementsProvider } from './AchievementsProvider';
import { BalanceProvider } from './BalanceProvider';

interface ModalProps {
  children: React.ReactNode;
  onClose?: () => void;
  maxWidthClass?: string;
}

interface ModalContextType {
  showModal: (modalProps: ModalProps) => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

/**
 * Provider component for managing a queue of modals.
 * @param {object} props - The props for the ModalProvider.
 * @param {ReactNode} props.children - The child components.
 */
export const ModalProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [modalQueue, setModalQueue] = useState<ModalProps[]>([]);

  const showModal = useCallback((modalProps: ModalProps) => {
    setModalQueue((prevQueue) => [...prevQueue, modalProps]);
  }, []);

  const closeCurrentModal = useCallback(() => {
    setModalQueue((prevQueue) => {
      const [currentModal, ...rest] = prevQueue;
      return rest;
    });
  }, []);

  const currentModal = modalQueue[0];

  return (
    <ModalContext.Provider value={{ showModal }}>
      <BalanceProvider>
        <AchievementsProvider>
          {children}
          {currentModal && (
            <Modal
              isOpen={true}
              onClose={() => {
                console.log('Modal onClose called');
                currentModal.onClose?.();
                closeCurrentModal();
              }}
              maxWidthClass={currentModal.maxWidthClass}
            >
              {React.cloneElement(currentModal.children as React.ReactElement, {
                onClose: () => {
                  currentModal.onClose?.();
                  closeCurrentModal();
                },
              })}
            </Modal>
          )}
        </AchievementsProvider>
      </BalanceProvider>
    </ModalContext.Provider>
  );
};

/**
 * Hook to access the modal context.
 * @returns {ModalContextType} The modal context value.
 * @throws {Error} If used outside of a ModalProvider.
 */
export const useModals = (): ModalContextType => {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error('useModals must be used within a ModalProvider');
  }
  return context;
};
